"use client";

import { useEffect, useMemo, useRef, useState, type FormEvent, type RefObject } from "react";
import Link from "next/link";
import { calculateNumerology } from "@/lib/api/numerology";
import type { NumerologyRequest, NumerologyResponse } from "@/lib/api/numerology";
import CalculatorCrossLinks from "@/components/CalculatorCrossLinks";
import { trackCalculatorUsed } from "@/lib/analytics";
import RatingPrompt from "@/components/RatingPrompt";
import { RESULT_REASONS } from "@/lib/feedback/resultReasons";
import { NumerologyHeader } from "./_components/NumerologyHeader";
import { NumberCard } from "./_components/NumberCard";

export default function NumerologyClient({
  defaultLanguage = "en",
}: {
  defaultLanguage?: "en" | "hi";
}) {
  const [name, setName] = useState("");
  const [birthDate, setBirthDate] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [result, setResult] = useState<NumerologyResponse | null>(null);
  const resultRef = useRef<HTMLDivElement>(null);

  // Cap date picker at today — no future-birth numerology.
  const todayIso = useMemo(() => new Date().toISOString().slice(0, 10), []);

  useEffect(() => {
    if (result && resultRef.current) {
      resultRef.current.scrollIntoView({ behavior: "smooth", block: "start" });
    }
  }, [result]);

  async function handleSubmit(e: FormEvent) {
    e.preventDefault();
    setLoading(true);
    setError(null);
    try {
      const data: NumerologyRequest = {
        name: name.trim(),
        birth_date: birthDate,
      };
      const response = await calculateNumerology(data);
      setResult(response);
      trackCalculatorUsed({ calculator: "numerology" });
    } catch (err) {
      setError(err instanceof Error ? err.message : "Something went wrong");
    } finally {
      setLoading(false);
    }
  }

  return (
    <div className="sacred-bg min-h-[calc(100vh-130px)]">
      <div className="mx-auto max-w-2xl px-4 sm:px-6 py-12 sm:py-16">
        <NumerologyHeader language={defaultLanguage} />

        {!result && (
          <NumerologyForm
            name={name}
            birthDate={birthDate}
            todayIso={todayIso}
            loading={loading}
            error={error}
            onNameChange={setName}
            onBirthDateChange={setBirthDate}
            onSubmit={handleSubmit}
          />
        )}

        {result && (
          <NumerologyResults
            result={result}
            resultRef={resultRef}
            onReset={() => {
              setResult(null);
              setError(null);
              setName("");
              setBirthDate("");
            }}
          />
        )}

        {result && (
          <div className="mt-6">
            <RatingPrompt
              source={"tool_result" as never}
              mode="thumbs+reasons"
              reasons={RESULT_REASONS}
              prompt="Was this numerology reading accurate?"
              context={{
                surface: "numerology_result",
                path: typeof window !== "undefined" ? window.location.pathname : null,
              }}
            />
          </div>
        )}

        <NumerologySeoContent />

        {/* Cross Links */}
        <CalculatorCrossLinks currentPage="numerology" />
      </div>
    </div>
  );
}

function NumerologyForm({
  name,
  birthDate,
  todayIso,
  loading,
  error,
  onNameChange,
  onBirthDateChange,
  onSubmit,
}: {
  name: string;
  birthDate: string;
  todayIso: string;
  loading: boolean;
  error: string | null;
  onNameChange: (value: string) => void;
  onBirthDateChange: (value: string) => void;
  onSubmit: (event: FormEvent) => void;
}) {
  return (
    <div className="mx-auto max-w-md">
      <form onSubmit={onSubmit} className="card-sacred p-6 sm:p-8 space-y-5">
        <div>
          <label
            htmlFor="num-name"
            className="block text-[13px] font-semibold mb-2"
            style={{ color: "var(--ish-dark)" }}
          >
            Your Full Name
          </label>
          <input
            id="num-name"
            type="text"
            required
            value={name}
            onChange={(event) => onNameChange(event.target.value)}
            placeholder="Enter your full name"
            className="input-sacred w-full rounded-lg px-4 py-3 text-[14px]"
            style={{ color: "var(--ish-dark)" }}
            autoComplete="name"
            autoCapitalize="words"
          />
          <p className="mt-1.5 text-[11px]" style={{ color: "var(--ish-text-faint)" }}>
            Used for Name Number calculation (Chaldean system)
          </p>
        </div>

        <div>
          <label
            htmlFor="num-date"
            className="block text-[13px] font-semibold mb-2"
            style={{ color: "var(--ish-dark)" }}
          >
            Birth Date
          </label>
          <input
            id="num-date"
            type="date"
            required
            value={birthDate}
            onChange={(event) => onBirthDateChange(event.target.value)}
            min="1900-01-01"
            max={todayIso}
            className="input-sacred w-full rounded-lg px-4 py-3 text-[14px]"
            style={{ color: "var(--ish-dark)" }}
            autoComplete="bday"
          />
        </div>

        <FormError error={error} />
        <NumerologySubmitButton loading={loading} />
      </form>
    </div>
  );
}

function FormError({ error }: { error: string | null }) {
  if (!error) return null;
  return (
    <div
      role="alert"
      aria-live="polite"
      className="text-[13px] rounded-lg p-3.5"
      style={{
        color: "var(--color-error-text)",
        backgroundColor: "var(--color-error-bg)",
        border: "1px solid var(--color-error-tint)",
      }}
    >
      {error}
    </div>
  );
}

function NumerologySubmitButton({ loading }: { loading: boolean }) {
  return (
    <button
      type="submit"
      disabled={loading}
      className="btn-sacred w-full font-semibold py-3.5 rounded-lg text-[14px]"
    >
      {loading ? (
        <span className="flex items-center justify-center gap-2.5">
          <span className="w-4 h-4 border-2 border-white/40 border-t-white rounded-full animate-spin" />
          Calculating...
        </span>
      ) : (
        <>Calculate My Numbers &rarr;</>
      )}
    </button>
  );
}

function NumerologyResults({
  result,
  resultRef,
  onReset,
}: {
  result: NumerologyResponse;
  resultRef: RefObject<HTMLDivElement | null>;
  onReset: () => void;
}) {
  return (
    <div ref={resultRef} className="space-y-6">
      <div className="grid grid-cols-1 gap-4">
        <NumberCard
          number={result.mulank}
          description="Derived from your birth day. Reveals your core personality and natural tendencies."
        />
        <NumberCard
          number={result.bhagyank}
          description="Derived from your full birth date. Shows your life path and destiny."
        />
        <NumberCard
          number={result.name_number}
          description="Derived from your name using Chaldean mapping. Reflects your outer personality."
        />
      </div>

      <div className="flex flex-col sm:flex-row gap-3 justify-center">
        <Link
          href="/free-kundali"
          className="btn-sacred text-center font-semibold py-3 px-6 rounded-lg text-[14px]"
        >
          Get Full Kundali Reading &rarr;
        </Link>
        <button
          onClick={onReset}
          className="text-center font-semibold py-3 px-6 rounded-lg text-[14px] transition-all duration-200"
          style={{
            color: "var(--ish-text-muted)",
            border: "1px solid var(--ish-border)",
          }}
        >
          Calculate Again
        </button>
      </div>
    </div>
  );
}

function NumerologySeoContent() {
  return (
    <div className="mt-12 card-sacred p-6 sm:p-8">
      <h2
        className="text-[20px] font-bold mb-4"
        style={{
          fontFamily: "var(--font-pt-serif), PT Serif, Georgia, serif",
          color: "var(--ish-dark)",
        }}
      >
        What is Vedic Numerology (Ank Jyotish)?
      </h2>
      <div className="space-y-3 text-[14px] leading-relaxed text-ish-text-muted">
        <p>
          <strong style={{ color: "var(--ish-dark)" }}>Vedic Numerology</strong> (Ank Jyotish) is an
          ancient Indian system that reveals the influence of numbers on your life. Unlike Western
          numerology, the Vedic system uses the Chaldean letter-to-number mapping, which is
          considered more accurate as it is based on the vibrational frequency of each letter.
        </p>
        <p>
          Three key numbers define your numerological profile:{" "}
          <strong style={{ color: "var(--ish-dark)" }}>Mulank</strong> (Root Number) from your birth
          day reveals your basic nature,{" "}
          <strong style={{ color: "var(--ish-dark)" }}>Bhagyank</strong> (Destiny Number) from your
          full birth date shows your life purpose, and{" "}
          <strong style={{ color: "var(--ish-dark)" }}>Namank</strong> (Name Number) from your name
          reflects how others perceive you.
        </p>
        <p>
          Each number (1-9) is ruled by a specific planet (graha) in Vedic astrology. For example, 1
          is ruled by Sun (Surya), 2 by Moon (Chandra), and 3 by Jupiter (Guru). Understanding your
          numbers helps you make better decisions about career, relationships, and spiritual growth.
        </p>
      </div>
    </div>
  );
}
